"use client"

import { useState } from "react"
import { downloadExcelTemplate } from "@/actions/fastapi"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { format } from "date-fns"
import { Info } from "lucide-react"

import { toast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

const safetyCertificateFormSchema = z.object({
  date_year: z.string(),
  date_month: z.string(),
  date_day: z.string(),
  architect_number_1: z.enum(["一級", "二級"]),
  architect_number_2: z.string().min(1, { message: "必須項目です。" }),
  architect_number_3: z.string().min(1, { message: "必須項目です。" }),
  architect_name: z.string().min(1, { message: "必須項目です。" }),
  office_1: z.string(),
  office_2: z.string(),
  office_3: z.string(),
  address: z
    .string()
    .min(5, { message: "住所は5文字以上で入力してください。" }),
  phone_number: z
    .string()
    .regex(
      /^\d{2,4}-\d{2,4}-\d{4}$/,
      "電話番号は正しい形式で入力してください。",
    ),
  client_name: z.string(),
  building_location: z.string(),
  building_name: z.string(),
  building_usage: z.string(),
  building_area: z.string(),
  total_area: z.string(),
  max_height: z.string(),
  eaves_height: z.string(),
  ground_floors: z.string(),
  underground_floors: z.string(),
  structure_type_1: z.enum([
    "木造",
    "鉄筋コンクリート造",
    "鉄骨造",
    "壁式鉄筋コンクリート造",
  ]),
  structure_type_2: z.enum([
    "なし",
    "木造",
    "鉄筋コンクリート造",
    "鉄骨造",
    "壁式鉄筋コンクリート造",
  ]),
  building_kubun: z.enum(["1", "2", "3", "4"]),
  keisan_kubun: z.enum(["1", "2", "3", "4", "5"]),
  keisan_nintei: z.enum(["1", "2"]),
  program_name: z.string(),
  program_nintei: z.enum(["1", "2"]),
  program_nintei_number: z.string(),
})

type SafetyCertificateFormValues = z.infer<typeof safetyCertificateFormSchema>

const today = new Date()
const defaultYear = format(today, "yyyy")
const defaultMonth = format(today, "M")
const defaultDay = format(today, "d")

const SafetyCertificateForm: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formData, setFormData] = useState<SafetyCertificateFormValues | null>(
    null,
  )
  const [activeTab, setActiveTab] = useState("basic")
  const form = useForm<SafetyCertificateFormValues>({
    resolver: zodResolver(safetyCertificateFormSchema),
    defaultValues: {
      date_year: defaultYear,
      date_month: defaultMonth,
      date_day: defaultDay,
      architect_number_1: "一級",
      architect_number_2: "大臣",
      architect_number_3: "111111",
      architect_name: "○○　○○",
      office_1: "一級",
      office_2: "東京都",
      office_3: "111111",
      address: "東京都港区芝５丁目２６−２０",
      phone_number: "00-0000-0000",
      client_name: "○○ ○○",
      building_location: "東京都港区芝５丁目２６−２０",
      building_name: "○○新築工事",
      building_usage: "一戸建ての住宅",
      building_area: "1000.00",
      total_area: "1000.00",
      max_height: "9.000",
      eaves_height: "6.000",
      ground_floors: "3",
      underground_floors: "1",
      structure_type_1: "木造",
      structure_type_2: "なし",
      building_kubun: "3",
      keisan_kubun: "5",
      keisan_nintei: "1",
      program_name: "Super Build/SS7",
      program_nintei: "2",
      program_nintei_number: "0",
    },
    mode: "onSubmit",
  })

  const handleSubmit = (data: SafetyCertificateFormValues) => {
    setFormData(data)
    setIsDialogOpen(true)
  }

  const handleDownload = async () => {
    if (!formData) return

    try {
      const response = await downloadExcelTemplate(
        "safety_certificate",
        formData,
      )

      if (response.excel_download_url) {
        toast({
          title: "Excelファイルが正常に生成されました。",
          description:
            "リンクをクリックしてファイルをダウンロードしてください。",
        })
        window.open(response.excel_download_url, "_blank")
      } else {
        toast({
          title: "エラーが発生しました",
          description: "ファイルのダウンロードリンクがありません。",
          variant: "destructive",
        })
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "エラーが発生しました",
          description: error.message,
          variant: "destructive",
        })
      }
    }

    setIsDialogOpen(false)
  }

  const formLabels: { [key: string]: string } = {
    date_year: "年",
    date_month: "月",
    date_day: "日",
    architect_number_1: "建築士種別",
    architect_number_2: "登録種別",
    architect_number_3: "登録番号",
    architect_name: "建築士氏名",
    office_1: "事務所種別",
    office_2: "都道府県",
    office_3: "登録番号",
    address: "所在地",
    phone_number: "電話番号",
    client_name: "委託者",
    building_location: "建築物の所在地",
    building_name: "建築物の名称",
    building_usage: "建築物の用途",
    building_area: "建築面積 (m²)",
    total_area: "延床面積 (m²)",
    max_height: "最高高さ (m)",
    eaves_height: "最高の軒高さ (m)",
    ground_floors: "地上階数",
    underground_floors: "地下階数",
    structure_type_1: "構造形式（地上）",
    structure_type_2: "構造形式（地下）",
    building_kubun: "建築物の区分",
    keisan_kubun: "構造計算の種類",
    keisan_nintei: "構造計算の方法",
    program_name: "プログラムの名称",
    program_nintei: "プログラムの国土交通大臣認定",
    program_nintei_number: "プログラム認定番号",
  }

  const selectItemValues: { [key: string]: { [key: string]: string } } = {
    architect_number_1: { 一級: "一級", 二級: "二級" },
    office_1: { 一級: "一級", 二級: "二級" },
    structure_type_1: {
      木造: "木造",
      鉄筋コンクリート造: "鉄筋コンクリート造",
      鉄骨造: "鉄骨造",
      壁式鉄筋コンクリート造: "壁式鉄筋コンクリート造",
    },
    structure_type_2: {
      なし: "なし",
      木造: "木造",
      鉄筋コンクリート造: "鉄筋コンクリート造",
      鉄骨造: "鉄骨造",
      壁式鉄筋コンクリート造: "壁式鉄筋コンクリート造",
    },
    building_kubun: {
      "1": "法第20条第１項第１号",
      "2": "法第20条第１項第２号",
      "3": "法第20条第１項第３号",
      "4": "法第20条第１項第４号",
    },
    keisan_kubun: {
      "1": "令第81条第１項に定める基準に従った構造計算",
      "2": "令第81条第２項第１号イに規定する構造計算",
      "3": "令第81条第２項第１号ロに規定する構造計算",
      "4": "令第81条第２項第２号イに規定する構造計算",
      "5": "令第81条第３項に定める基準に従った構造計算",
    },
    keisan_nintei: {
      "1": "国土交通大臣が定めた方法によるもの",
      "2": "国土交通大臣の認定を受けたプログラムによるもの",
    },
    program_nintei: { "1": "有", "2": "無" },
  }

  const formatFormData = (data: SafetyCertificateFormValues) => {
    return Object.entries(data).map(([key, value]) => (
      <div key={key} className="grid grid-cols-2 gap-2">
        <span className="font-semibold">{formLabels[key] || key}:</span>
        <span>
          {key in selectItemValues
            ? selectItemValues[key][value as string] || value
            : value}
        </span>
      </div>
    ))
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">基本情報</TabsTrigger>
              <TabsTrigger value="architect">建築士</TabsTrigger>
              <TabsTrigger value="office">事務所</TabsTrigger>
              <TabsTrigger value="building">建築物</TabsTrigger>
              <TabsTrigger value="calculation">構造計算</TabsTrigger>
            </TabsList>
            <TabsContent value="basic">
              <Card>
                <CardHeader>
                  <CardTitle>基本情報</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="date_year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>年</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Year" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {[...Array(10)].map((_, i) => (
                                <SelectItem
                                  key={i}
                                  value={(parseInt(defaultYear) + i).toString()}
                                >
                                  {parseInt(defaultYear) + i}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="date_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>月</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Month" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {[...Array(12)].map((_, i) => (
                                <SelectItem key={i} value={(i + 1).toString()}>
                                  {i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="date_day"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>日</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Day" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {[...Array(31)].map((_, i) => (
                                <SelectItem key={i} value={(i + 1).toString()}>
                                  {i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="architect">
              <Card>
                <CardHeader>
                  <CardTitle>建築士情報</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="architect_number_1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>建築士種別</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="一級 or 二級" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="一級">一級</SelectItem>
                              <SelectItem value="二級">二級</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="architect_number_2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>登録種別</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="大臣" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="architect_number_3"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>登録番号</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="111111" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="architect_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>建築士氏名</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="○○　○○" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="office">
              <Card>
                <CardHeader>
                  <CardTitle>建築士事務所情報</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="office_1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>事務所種別</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="一級 or 二級" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="一級">一級</SelectItem>
                              <SelectItem value="二級">二級</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="office_2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>都道府県</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="都道府県" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="office_3"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>登録番号</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="111111" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>所在地</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="住所" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>電話番号</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="00-0000-0000" />
                        </FormControl>
                        <FormDescription>
                          ハイフン（-）を含めて入力してください。
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="building">
              <Card>
                <CardHeader>
                  <CardTitle>建築物情報</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="client_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>委託者</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="○○ ○○" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="building_location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>建築物の所在地</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="東京都港区芝５丁目２６−２０"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="building_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>建築物の名称</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="○○新築工事" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="building_usage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>建築物の用途</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="一戸建ての住宅" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="building_area"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>建築面積 (m²)</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="1000.00" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="total_area"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>延床面積 (m²)</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="1000.00" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="max_height"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>最高高さ (m)</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="9.000" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="eaves_height"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>最高の軒高さ (m)</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="6.000" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="ground_floors"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>地上階数</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="3" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="underground_floors"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>地下階数</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="1" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="structure_type_1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>構造形式（地上）</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="○○造" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="木造">木造</SelectItem>
                              <SelectItem value="鉄筋コンクリート造">
                                鉄筋コンクリート造
                              </SelectItem>
                              <SelectItem value="鉄骨造">鉄骨造</SelectItem>
                              <SelectItem value="壁式鉄筋コンクリート造">
                                壁式鉄筋コンクリート造
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="structure_type_2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>構造形式（地下）</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="○○造" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="なし">なし</SelectItem>
                              <SelectItem value="木造">木造</SelectItem>
                              <SelectItem value="鉄筋コンクリート造">
                                鉄筋コンクリート造
                              </SelectItem>
                              <SelectItem value="鉄骨造">鉄骨造</SelectItem>
                              <SelectItem value="壁式鉄筋コンクリート造">
                                壁式鉄筋コンクリート造
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="calculation">
              <Card>
                <CardHeader>
                  <CardTitle>構造計算情報</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="building_kubun"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          建築物の区分
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 inline-block ml-1" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  建築基準法第20条に基づく建築物の区分を選択してください。
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Building Kubun" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">
                              法第20条第１項第１号
                            </SelectItem>
                            <SelectItem value="2">
                              法第20条第１項第２号
                            </SelectItem>
                            <SelectItem value="3">
                              法第20条第１項第３号
                            </SelectItem>
                            <SelectItem value="4">
                              法第20条第１項第４号
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="keisan_kubun"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>構造計算の種類</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Keisan Kubun" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">
                              令第81条第１項に定める基準に従った構造計算
                            </SelectItem>
                            <SelectItem value="2">
                              令第81条第２項第１号イに規定する構造計算
                            </SelectItem>
                            <SelectItem value="3">
                              令第81条第２項第１号ロに規定する構造計算
                            </SelectItem>
                            <SelectItem value="4">
                              令第81条第２項第２号イに規定する構造計算
                            </SelectItem>
                            <SelectItem value="5">
                              令第81条第３項に定める基準に従った構造計算
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="keisan_nintei"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>構造計算の方法</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Keisan Nintei" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">
                              国土交通大臣が定めた方法によるもの
                            </SelectItem>
                            <SelectItem value="2">
                              国土交通大臣の認定を受けたプログラムによるもの
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="program_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>プログラムの名称</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Super Build/SS7"
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="program_nintei"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>プログラムの国土交通大臣認定</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Program Nintei" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">有</SelectItem>
                            <SelectItem value="2">無</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="program_nintei_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>プログラム認定番号</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="0"
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          <div className="sticky bottom-0 bg-background p-4 shadow-md">
            <Button type="submit" className="w-full">
              エクセルをダウンロード
            </Button>
          </div>
        </form>
      </Form>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>入力内容の確認</DialogTitle>
            <DialogDescription>
              以下の内容でエクセルファイルを生成します。よろしいですか？
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {formData && formatFormData(formData)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleDownload}>ダウンロード</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SafetyCertificateForm
